import { useDispatch, useSelector } from "react-redux";
import { useGetBranchesQuery } from "../admin/adminApi";
import { useClaims } from "../auth/useClaims";
import { branchSelected, selectHasChosenBranch, selectSelectedBranchId } from "./branchFilterSlice";

// Drives the header BranchPicker and every branch-scoped list screen
// (Customers, Products, Employees, Users - handoff §13.1). Options are the
// branches on the logged-in user's own token, resolved to {branchId} via the
// admin/branches catalog; an all-branch user also gets an explicit "All
// branches" choice, a branch-scoped user just sees their own branch(es) and
// the picker is effectively a label (server already no-ops a redundant
// branchId= for them).
export const useBranchFilter = () => {
  const dispatch = useDispatch();
  const selectedBranchId = useSelector(selectSelectedBranchId);
  const hasChosen = useSelector(selectHasChosenBranch);
  const { branches: callerBranchCodes, isAllBranches, homeBranch } = useClaims();
  const { data: allBranches = [] } = useGetBranchesQuery();

  const availableBranches = isAllBranches
    ? allBranches
    : allBranches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  // The branch actually in effect. The stored value starts at null ("All
  // branches") for everyone, but only an all-branch user HAS an "All" option -
  // for anyone else null was a phantom state. Found live 2026-09-10: liju
  // (SYSTEMS only) saw a SYSTEMS badge in the header while every page got null,
  // so Stock Balance printed "Branch: All" under the Texas Alarms logo and name;
  // and a multi-branch user's picker displayed its first branch while the data
  // silently covered all of them ("the global branch filter is not picked").
  // So for a non-all-branch user, a missing or no-longer-valid choice resolves
  // to their first branch - what the picker already shows. Every one of the ~66
  // branch-scoped screens reads this hook, so fixing it here fixes them all.
  //
  // Before the user picks anything, open on their MOTHER branch - the linked
  // employee's own branch, sent by the API - even when they can see several or
  // all branches (asked 2026-09-10). It can't be taken from the branch list's
  // order: that is sorted by BranchId, so "first" meant MOMBASA for anyone who
  // has it (BRIAN, a NAIROBI employee, opened on MOMBASA). An account with no
  // employee (admin, CONTROLMSA) keeps the old default: All branches when it
  // has them, otherwise its first branch.
  const storedIsValid =
    selectedBranchId != null && availableBranches.some((b) => b.branchId === selectedBranchId);
  const homeBranchId = homeBranch
    ? availableBranches.find((b) => b.branchCode === homeBranch)?.branchId
    : undefined;
  const firstBranchId = availableBranches[0]?.branchId ?? null;

  // A user with SEVERAL (not all) branches may also pick "All my branches" (null) -
  // the server then covers exactly their own branches. Found 2026-09-17: diya
  // (MOMBASA/NAIROBI/SYSTEMS) opened on MOMBASA and could not find her own Nairobi
  // LPO without knowing to switch the header branch. Single-branch users still
  // never get null (the phantom "All" of 09-10).
  const canPickAll = isAllBranches || availableBranches.length > 1;

  let effectiveBranchId;
  if (hasChosen) {
    effectiveBranchId =
      isAllBranches || (canPickAll && selectedBranchId == null) || storedIsValid ? selectedBranchId : firstBranchId;
  } else {
    effectiveBranchId = homeBranchId ?? (isAllBranches ? null : firstBranchId);
  }

  const setSelectedBranchId = (branchId) => dispatch(branchSelected(branchId));

  return {
    selectedBranchId: effectiveBranchId,
    setSelectedBranchId,
    availableBranches,
    showAllOption: canPickAll,
    // "All branches" for an all-branch account, "All my branches" otherwise.
    allOptionLabel: isAllBranches ? "All branches" : "All my branches",
  };
};
